import React, { useCallback, useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'utils/redux'
import moment from 'moment'
import { DealDetail, DefaultCollateralIdentifierType, EntityType, EntityTypeEnum } from 'api'
import { useModal } from 'view/shared/modal'
import { CenterPanel } from 'view/routes/routesLayout'
import { DataGrid, DataGridProps, filteredColumnDefsBuilder, GridAutoSizing, GridId } from 'view/components/datagrid'
import { dealsGridColumnDefs, dealsGridOptions } from '../DealsGridModel'
import { OrganisationFilterSwitch } from 'view/components/datagrid/toolbar/OrganisationFilterSwitch'
import { DealsGroupByToggles } from './DealsGroupByToggles'
import { DealsDownloadDialog } from './DealsDownloadDialog'
import { RelatedPanel } from './RelatedPanel'
import { AdminOnlyColumnIds, CollateralBasketColumnId, CollectionColumnId } from 'view/routes/ColumnNames'
import { EnvironmentContext } from 'view/shared/environment'
import { createContingencyFileContent } from 'model/dealUtils'
import { downloadCsv } from 'utils'
import { relatedDealDetailsSelector } from 'selectors/dealSelectors'
import { SubscriptionDetail } from 'store/selection/selectionTypes'
import { GridApi } from 'ag-grid-community'
import { DealsGridColumnId } from '../DealsGridColumnNames'
import { SalesCoverFilterSwitch } from 'view/components/datagrid/toolbar/SalesCoverFilterSwitch'
import {
  startConsiderationOverrideEnabledSelector,
  isStockBorrowingLendingEnabledSelector,
  isEvergreenNoticeEnabledSelector
} from 'selectors/featureSelectors'
import { useAllEntities, useIsAdmin, useIsCurrentOrganisationBuySide, useIsSnapshotLoaded, useUserPreference } from 'view/hooks'
import { RelatedDealsThunks } from 'thunks'

interface DealsScreenBaseProps {
  children: React.ReactNode
  customToolbarButtons?: DataGridProps<DealDetail>['customToolbarButtons']
  dealDetails: DealDetail[]
  gridId: GridId
  customToolbarComponents?: DataGridProps<DealDetail>['customToolbarComponents']
  onPageChanged: (page: number) => void
  onAfterRestoreDefaultLayout?: (gridApi: GridApi) => void
  subscriptionDetail: SubscriptionDetail | undefined
  subscriptionFilter: JSX.Element
  exportFileName?: string
}

export const DealsScreenBase: React.FC<DealsScreenBaseProps> = ({
  dealDetails,
  subscriptionDetail,
  onAfterRestoreDefaultLayout,
  children,
  subscriptionFilter,
  onPageChanged,
  gridId,
  customToolbarButtons,
  customToolbarComponents,
  exportFileName
}) => {
  const dispatch = useAppDispatch()
  const { showModal } = useModal()
  const isBuySideOrganisation = useIsCurrentOrganisationBuySide()
  const snapshotLoaded = useIsSnapshotLoaded(EntityTypeEnum.DEAL)
  const isAdminUser = useIsAdmin()
  const userPreference = useUserPreference()
  const relatedDealDetails = useSelector(relatedDealDetailsSelector)
  const evergreenNoticeEnabled = useSelector(isEvergreenNoticeEnabledSelector)
  const startConsiderationOverrideEnabled = useSelector(startConsiderationOverrideEnabledSelector)
  const stockBorrowingLendingEnabled = useSelector(isStockBorrowingLendingEnabledSelector)
  const clearingHouseInteractions = useAllEntities(EntityType.CLEARING_HOUSE_INTERACTION)

  const columnDefs = useMemo(
    () =>
      filteredColumnDefsBuilder(dealsGridColumnDefs(isAdminUser, isBuySideOrganisation))
        .withoutIf(!evergreenNoticeEnabled, [DealsGridColumnId.EVERGREEN_NOTICE_PERIOD])
        .withoutIf(!isAdminUser, Object.values(AdminOnlyColumnIds))
        .withoutIf(!startConsiderationOverrideEnabled, [DealsGridColumnId.DEAL_START_CONSIDERATION_OVERRIDE])
        .withoutIf(!stockBorrowingLendingEnabled, [
          DealsGridColumnId.DEAL_FEE,
          DealsGridColumnId.SECONDARY_COLLATERAL_NAME,
          DealsGridColumnId.DEAL_STOCK_BORROWING_LENDING_TYPE
        ])
        .build(),
    [evergreenNoticeEnabled, isAdminUser, isBuySideOrganisation, startConsiderationOverrideEnabled, stockBorrowingLendingEnabled]
  )

  const onExportContingencyFile = useCallback(
    (clearingHouseId: string) => {
      const content = createContingencyFileContent(dealDetails, clearingHouseInteractions, clearingHouseId)
      downloadCsv(content, 'contingency.csv')
    },
    [dealDetails, clearingHouseInteractions]
  )

  const handleAdminExport = useCallback(
    (exportCsv: () => unknown) => {
      showModal(<DealsDownloadDialog onExportCsv={exportCsv} onExportContingencyFile={onExportContingencyFile} />)
    },
    [showModal, onExportContingencyFile]
  )

  const environment = useContext(EnvironmentContext)

  const groupSortComparator = (groupAItems: DealDetail[], groupBItems: DealDetail[]) => {
    const groupACreatedAt = moment.max(groupAItems.map(dealDetail => moment(dealDetail.deal.createdAt)))
    const groupBCreatedAt = moment.max(groupBItems.map(dealDetail => moment(dealDetail.deal.createdAt)))

    if (groupACreatedAt.isBefore(groupBCreatedAt)) {
      return 1
    } else if (groupACreatedAt.isAfter(groupBCreatedAt)) {
      return -1
    } else {
      return 0
    }
  }

  const handleRelatedOnClose = () => {
    dispatch(RelatedDealsThunks.updateRelatedDealsSubscription(null))
  }

  const handleAfterRestoreDefaultLayout = (gridApi: GridApi) => {
    gridApi.setColumnsVisible(
      [DealsGridColumnId.COLLATERAL_ISIN_OR_BUI],
      userPreference.defaultCollateralIdentifierType !== DefaultCollateralIdentifierType.CUSIP
    )

    gridApi.setColumnsVisible(
      [DealsGridColumnId.COLLATERAL_CUSIP],
      userPreference.defaultCollateralIdentifierType !== DefaultCollateralIdentifierType.ISIN
    )

    onAfterRestoreDefaultLayout?.(gridApi)
  }

  return (
    <CenterPanel>
      {children}
      <DataGrid
        gridId={gridId}
        gridClass="deals-grid"
        gridOptions={dealsGridOptions}
        gridColumnAutoSizing={GridAutoSizing.IGNORE_HEADERS}
        columnDefs={columnDefs}
        rowData={dealDetails}
        customToolbarComponents={customToolbarComponents}
        customToolbarSwitches={[OrganisationFilterSwitch, SalesCoverFilterSwitch]}
        customToolbarButtons={customToolbarButtons}
        GroupByToggles={DealsGroupByToggles}
        defaultGroupSortComparator={groupSortComparator}
        enforcedGroupingColStates={[{ colId: CollateralBasketColumnId.COLLATERAL_BASKET_DEAL_ID, sort: 'asc' }]}
        allPossibleLockedPrimaryRowGroupColumnIds={[CollectionColumnId.COLLECTION_NAME, CollateralBasketColumnId.COLLATERAL_BASKET_DEAL_ID]}
        subscriptionFilters={() => subscriptionFilter}
        subscriptionDetail={subscriptionDetail}
        onPageChanged={onPageChanged}
        showLoader={!snapshotLoaded}
        handleExport={environment.isAdmin ? handleAdminExport : undefined}
        onAfterRestoreDefaultLayout={handleAfterRestoreDefaultLayout}
        exportFileName={exportFileName}
      />
      <RelatedPanel dealDetails={relatedDealDetails} parentGridId={gridId} onClose={handleRelatedOnClose} />
    </CenterPanel>
  )
}
